import { Injectable } from '@angular/core';
import { Folder } from '../models/site-content/folder.model';

// Define an interface that allows for notifying objects of folder value changes.
export interface IFolderSelectionChangedListener {
    syncWithFolderChanges(): unknown;
    folderSelectionChanged(folder: Folder): void;
    get Route(): string;
}

/*
 * This is a singleton service used as the single source of the current folder of the Structure screen
 * It was added for VNEXT-111
*/

@Injectable({
    providedIn: 'root' // Note:  this directive makes this service a singleton.
})
export class CurrentFolderService {

    // Properties.
    // PJH - 3/9/2026 - for VNEXT-2290 it became neccessary to prevent calls to listeners which belong to routes 
    // that are in a detached state (that is, currently stored/cached in memory) and so detachedRoutes was added.
    // This relates to route reuse and our CustomRouteReuseStrategy ~ See https://community-dc.max.gov/x/IYI3og
    private detachedRoutes: string[] = [];
    private folder: Folder;
    private folderChangedListeners: IFolderSelectionChangedListener[] = [];

    // Constructor.
    constructor() { }

    public set Folder(folder: Folder) {
        this.folder = folder;
        for (let index: number = 0; index < this.folderChangedListeners.length; index++) {
            const listener = this.folderChangedListeners[index];
            // only call folderSelectionChanged() on components belonging to attached (aka NOT currently cached) routes
            if (this.routeIsAttached(listener)) {
                listener.folderSelectionChanged(this.folder);
            }
        }
    }

    // Not used as of 3/9/2026
    public NotifyListeners() {
        for (let index: number = 0; index < this.folderChangedListeners.length; index++) {
            const listener = this.folderChangedListeners[index];
            if (this.routeIsAttached(listener)) {
                listener.syncWithFolderChanges();
            }
        }
    }

    public removeFromDetachedRoutes(path: string) {
        if (this.detachedRoutes)
            this.detachedRoutes = this.detachedRoutes.filter(x => x != path);
    }

    public addToDetachedRoutes(path: string) {
        if (!this.detachedRoutes)
            this.detachedRoutes = [];

        if (this.detachedRoutes.indexOf(path) < 0)
            this.detachedRoutes.push(path);
    }

    public get Folder(): Folder {
        return this.folder;
    }

    public nullifyCurrentFolder() {
        this.folder = null;
    }

    // If this.folder's id property matches that of the id argument, returns true, otherwise returns false
    public isCurrentFolder(id: number): boolean {
        if (this.folder != null && this.folder.id == id) {
            return true;
        } else {
            return false;
        }
    }

    // Register for folder selection changes.
    // This gets called by Forms Grids when their "Connect Forms Grid to Folder Tree" option is checked
    public registerFolderChangedListener(listener: IFolderSelectionChangedListener): void {
        this.folderChangedListeners.push(listener);
    }
    public removeFolderChangedListener(listener: IFolderSelectionChangedListener) {
        this.folderChangedListeners = this.folderChangedListeners.filter(l => l != listener);
    }

    // Returns true if the IFolderSelectionChangedListener's route is NOT found in the array
    // of detached routes
    private routeIsAttached(listener: IFolderSelectionChangedListener): boolean {
        let attached = true;
        for (const r of this.detachedRoutes) {
            if (listener.Route.includes(r)) {
                attached = false;
                break;
            }
        }
        return attached;
    }
}
